<template>
  <div>
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start mb-1">
      <div>
        <h4 class="font-weight-bold">250k+ Deposits</h4>
        <div class="text-muted">
          High-value depositors who have crossed the $250k lifetime threshold
          (AML/Compliance)
        </div>
      </div>

      <div>
        <b-button
          variant="primary"
          @click="handleExport"
          :disabled="loadingDeposits250kPlus"
        >
          <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
          Export
        </b-button>
      </div>
    </div>

    <!-- STATS -->
    <b-card>
      <div>
        <b-row>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Total Users</div>
              <div class="h5 font-weight-bold">{{ metadata.total || 0 }}</div>
            </div>
          </b-col>
          <b-col cols="12" sm="6">
            <div class="stat-box">
              <div class="text-muted small">Crossed 250k</div>
              <div class="h5 font-weight-bold text-danger">
                {{ deposits250kPlus.filter((x) => x.crossed250kFlag).length }}
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- TABLE -->
    <b-card no-body>
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="d-inline-block mx-1"
              style="width: 100px"
            />
            <label>entries</label>
          </b-col>
          <b-col cols="12" md="6" class="text-right">
            <b-form-input v-model="search" placeholder="Search by email..." />
          </b-col>
        </b-row>
      </div>

      <b-table
        :items="filteredItems"
        :fields="tableFields"
        show-empty
        responsive
        :busy="loadingDeposits250kPlus"
      >
        <template #cell(userId)="{ item }">
          <b-link :to="{ name: 'customers-view', params: { id: item.userId } }">
            {{ item.userId }}
          </b-link>
        </template>

        <template #cell(name)="{ item }">
          {{ item.firstName }} {{ item.lastName }}
        </template>

        <template #cell(lifetimeDeposits)="{ item }">
          <span class="font-weight-semibold">{{
            formatCurrencyBuiltInFunc(item.lifetimeDeposits)
          }}</span>
        </template>

        <template #cell(crossed250kFlag)="{ item }">
          <b-badge :variant="item.crossed250kFlag ? 'danger' : 'secondary'">
            {{ item.crossed250kFlag ? "CROSSED" : "Below" }}
          </b-badge>
        </template>

        <template #cell(crossed250kAt)="{ item }">
          <div v-if="item.crossed250kAt" class="small">
            {{ formatDate(item.crossed250kAt) }}
          </div>
          <div v-else class="text-muted small">-</div>
        </template>
      </b-table>

      <div class="mx-2 mb-2 mt-2" v-if="metadata.total > 0">
        <b-row>
          <b-col cols="12" sm="6">
            <span class="text-muted">
              Showing {{ (currentPage - 1) * perPage + 1 }} to
              {{ Math.min(currentPage * perPage, metadata.total) }} of
              {{ metadata.total }} entries
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex justify-content-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="metadata.total"
              :per-page="perPage"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BLink,
  BBadge,
  BFormInput,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapState } from "vuex";
import { format } from "date-fns";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BLink,
    BBadge,
    BFormInput,
    vSelect,
    BPagination,
  },
  data() {
    return {
      currentPage: 1,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],
      search: "",
      tableFields: [
        { key: "userId", label: "User ID" },
        { key: "email", label: "Email" },
        { key: "name", label: "Name" },
        { key: "lifetimeDeposits", label: "Lifetime Deposits" },
        { key: "crossed250kFlag", label: "Status" },
        { key: "crossed250kAt", label: "Crossed Date" },
      ],
      metadata: { total: 0 },
    };
  },
  computed: {
    ...mapState("reporting", [
      "deposits250kPlus",
      "loadingDeposits250kPlus",
      "deposits250kPlusPagination",
    ]),
    filteredItems() {
      if (!this.search) return this.deposits250kPlus;
      const s = this.search.toLowerCase();
      return this.deposits250kPlus.filter(
        (item) =>
          item.email?.toLowerCase().includes(s) ||
          item.firstName?.toLowerCase().includes(s) ||
          item.lastName?.toLowerCase().includes(s)
      );
    },
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchData();
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("reporting", ["getDeposits250kPlus"]),
    formatCurrencyBuiltInFunc,
    async fetchData() {
      const data = await this.getDeposits250kPlus({
        page: this.currentPage,
        limit: this.perPage,
      });
      if (data?.pagination) {
        this.metadata = {
          total: data.pagination.total || 0,
        };
      }
    },
    formatDate(date) {
      return format(new Date(date), "dd/MM/yyyy");
    },

    handleExport() {
      alert("Export functionality to be implemented");
    },
  },
};
</script>

<style scoped>
.stat-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}
</style>
